import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { Login, UserGroup } from '../../apis/api-client';
import { groupsClient, usersClient } from '../../apis/client-instances';
import UserAutocomplete from '../common/UserAutocomplete';
import ResetPasswordDialog from '../students/ResetPasswordDialog';
import AssignContentDialog from '../users/AssignContentDialog';
import {
  AddIcon,
  BookmarkIcon,
  CloseIcon,
  DeleteIcon,
  EditIcon,
  EmailIcon,
  FileTextIcon,
  LockResetIcon,
  MoreVertIcon,
  PersonAddIcon,
  PersonIcon,
  PersonOffIcon,
  SearchIcon,
  UploadIcon,
} from '../icons';
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  SelectField,
  TextField,
  Tooltip,
  Typography,
} from '../ui';
import { alert } from '../../hooks/function';
import { formatDateTime } from '../../utils/roleHelpers';

type MemberSort = 'name-asc' | 'name-desc' | 'joined-desc' | 'joined-asc' | 'login-desc' | 'login-asc';

const MEMBER_SORT_OPTIONS: { label: string; value: MemberSort }[] = [
  { label: 'Name (A → Z)', value: 'name-asc' },
  { label: 'Name (Z → A)', value: 'name-desc' },
  { label: 'Last sign-on (recent)', value: 'login-desc' },
  { label: 'Last sign-on (oldest)', value: 'login-asc' },
  { label: 'Joined (newest)', value: 'joined-desc' },
  { label: 'Joined (oldest)', value: 'joined-asc' },
];

interface GroupMembersTabProps {
  groupId: number;
  /** Seat cap (null = unlimited). Derived from the group's saved access
   *  in the page; used to gate adds + show the seat chip. */
  seatLimit: number | null;
  /** Owner user id — surfaces the "Owner" chip on the matching row. */
  ownerUserId?: number;
  /** Group name for the add-learner dialog copy. */
  groupName?: string;
  /** Reports the current member count up so the page's General-tab
   *  seat-remaining caption stays in sync (mirrors UsersDirectory's
   *  onTotalCountChange). */
  onMemberCountChange?: (count: number) => void;
}

/**
 * GroupMembersTab
 *
 * The Learners tab for the group editor: add learners individually or by
 * bulk CSV import, list them with search + sort, and run the per-row
 * actions (assign content, edit, reset password, resend invite, toggle
 * access, remove from group). Extracted from GroupEdit as a SELF-OWNING
 * component — it owns the member list + its lazy fetch and reports the
 * count up; the only things it needs down are the seat cap, the owner id,
 * and the group name.
 */
export default function GroupMembersTab({
  groupId,
  seatLimit,
  ownerUserId,
  groupName,
  onMemberCountChange,
}: GroupMembersTabProps) {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [members, setMembers] = useState<UserGroup[]>([]);
  // Per-user enrichment (lastLoginAt, etc.) fetched in bulk after the
  // member list lands. UserGroup itself only carries id/email/name.
  const [memberDetails, setMemberDetails] = useState<Record<number, Login>>({});
  const [membersLoading, setMembersLoading] = useState(false);
  const [pickedLearner, setPickedLearner] = useState<Login | null>(null);
  const [memberSaving, setMemberSaving] = useState(false);
  const [rowMenu, setRowMenu] = useState<{ anchorEl: HTMLElement; row: UserGroup } | null>(null);
  const closeRowMenu = () => setRowMenu(null);
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
  const [resetPasswordTarget, setResetPasswordTarget] = useState<Login | null>(null);
  const [assignTarget, setAssignTarget] = useState<Login | null>(null);
  const [addLearnerOpen, setAddLearnerOpen] = useState(false);
  const [bulkImportOpen, setBulkImportOpen] = useState(false);
  const [bulkImportFile, setBulkImportFile] = useState<File | null>(null);
  const [memberFilter, setMemberFilter] = useState('');
  const [memberSort, setMemberSort] = useState<MemberSort>('name-asc');

  const seatsRemaining = (additional: number) =>
    seatLimit == null ? Infinity : Math.max(0, seatLimit - (members.length + additional));

  // Synthesizes a Login-shaped object from a UserGroup row. Prefer the
  // enriched Login when present; otherwise the row carries enough.
  const loginFromRow = (m: UserGroup): Login => {
    const detail = m.userId ? memberDetails[m.userId] : undefined;
    if (detail) return detail;
    return {
      id: m.userId,
      email: m.userEmail,
      firstName: m.userFirstName,
      lastName: m.userLastName,
    } as Login;
  };

  const memberHandleEdit = (m: UserGroup) => {
    if (m.userId) navigate(`/students/${m.userId}`);
  };
  const memberHandleResetPassword = (m: UserGroup) => {
    setResetPasswordTarget(loginFromRow(m));
    setResetPasswordOpen(true);
  };
  const memberHandleResendInvitation = async (m: UserGroup) => {
    if (!m.userId) return;
    try {
      await usersClient.resendInvitation(m.userId);
      alert(enqueueSnackbar, `Invitation email resent to ${m.userEmail || 'user'}`, 'success');
    } catch (err: any) {
      alert(enqueueSnackbar, err?.response?.data || 'Failed to resend invitation', 'error');
    }
  };
  const memberHandleToggleActive = async (m: UserGroup) => {
    if (!m.userId) return;
    const current = memberDetails[m.userId];
    const wasActive = current?.isActive ?? true;
    try {
      await usersClient.toggleUserStatus(m.userId, !wasActive);
      setMemberDetails((cur) => ({
        ...cur,
        [m.userId!]: { ...(current ?? {}), isActive: !wasActive } as Login,
      }));
      alert(enqueueSnackbar, wasActive ? 'Access disabled' : 'Access enabled', 'success');
    } catch (err: any) {
      alert(enqueueSnackbar, err?.response?.data || 'Failed to update access', 'error');
    }
  };

  const filteredMembers = useMemo(() => {
    const q = memberFilter.trim().toLowerCase();
    if (!q) return members;
    return members.filter((m) => {
      const name = [m.userFirstName, m.userLastName].filter(Boolean).join(' ').toLowerCase();
      return name.includes(q) || (m.userEmail || '').toLowerCase().includes(q);
    });
  }, [members, memberFilter]);
  const sortedMembers = useMemo(() => {
    const copy = [...filteredMembers];
    const nameOf = (m: UserGroup) =>
      ([m.userLastName, m.userFirstName].filter(Boolean).join(' ') || m.userEmail || '').toLowerCase();
    const joinedOf = (m: UserGroup) => (m.createdAt ? new Date(m.createdAt as any).getTime() : 0);
    const lastLoginOf = (m: UserGroup) => {
      const lastLogin = m.userId ? memberDetails[m.userId]?.lastLoginAt : null;
      return lastLogin ? new Date(lastLogin as any).getTime() : 0;
    };
    copy.sort((a, b) => {
      switch (memberSort) {
        case 'name-asc':
          return nameOf(a).localeCompare(nameOf(b));
        case 'name-desc':
          return nameOf(b).localeCompare(nameOf(a));
        case 'joined-desc':
          return joinedOf(b) - joinedOf(a);
        case 'joined-asc':
          return joinedOf(a) - joinedOf(b);
        case 'login-desc':
          return lastLoginOf(b) - lastLoginOf(a);
        case 'login-asc':
          return lastLoginOf(a) - lastLoginOf(b);
      }
    });
    return copy;
  }, [filteredMembers, memberSort, memberDetails]);

  // Load members + enrich with Login details. One round-trip total. The
  // component only mounts when the Learners tab is active, so no tab guard.
  useEffect(() => {
    if (!groupId) return;
    let cancelled = false;
    (async () => {
      setMembersLoading(true);
      try {
        const result = await groupsClient.getGroupMembers(groupId);
        if (cancelled) return;
        setMembers(result ?? []);

        const ids = (result ?? []).map((m) => m.userId).filter((x): x is number => !!x);
        if (ids.length > 0) {
          try {
            const usersPage = await usersClient.searchUsersPaged(
              undefined,
              undefined,
              undefined,
              undefined,
              1,
              Math.max(ids.length, 50),
              ids,
            );
            if (cancelled) return;
            const map: Record<number, Login> = {};
            for (const u of usersPage.items ?? []) {
              if (u.id) map[u.id] = u;
            }
            setMemberDetails(map);
          } catch {
            // Detail enrichment is best-effort.
            if (!cancelled) setMemberDetails({});
          }
        } else {
          setMemberDetails({});
        }
      } catch {
        if (!cancelled) {
          alert(enqueueSnackbar, 'Failed to load learners', 'error');
          setMembers([]);
          setMemberDetails({});
        }
      } finally {
        if (!cancelled) setMembersLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [groupId, enqueueSnackbar]);

  // Keep the page's seat-remaining caption in sync as the roster changes.
  useEffect(() => {
    onMemberCountChange?.(members.length);
  }, [members, onMemberCountChange]);

  const handleAddPickedLearner = async () => {
    if (!pickedLearner?.id) return;
    if (members.some((m) => m.userId === pickedLearner.id)) {
      alert(enqueueSnackbar, 'Already a learner in this group', 'warning');
      return;
    }
    if (seatsRemaining(0) < 1) {
      alert(enqueueSnackbar, `Seat limit reached (${seatLimit}).`, 'error');
      return;
    }
    setMemberSaving(true);
    try {
      await groupsClient.addUsersToGroup(groupId, { userIds: [pickedLearner.id] });
      // Refetch so we get the canonical UserGroup shape (id, joinedAt, etc.)
      const refreshed = await groupsClient.getGroupMembers(groupId);
      setMembers(refreshed ?? []);
      setPickedLearner(null);
      setAddLearnerOpen(false);
      alert(enqueueSnackbar, 'Added to group', 'success');
    } catch (err: any) {
      alert(enqueueSnackbar, err?.response?.data || 'Failed to add learner', 'error');
    } finally {
      setMemberSaving(false);
    }
  };

  // Extract deduped lowercase emails from arbitrary spreadsheet text.
  const parseEmailsFromText = (text: string): string[] => {
    const emailRe = /[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/gi;
    const matches = text.match(emailRe) || [];
    const seen = new Set<string>();
    for (const m of matches) seen.add(m.toLowerCase());
    return [...seen];
  };

  const handleBulkImport = async () => {
    if (!bulkImportFile) return;
    const fileName = bulkImportFile.name.toLowerCase();
    // .xlsx isn't text-parseable without a library — bounce with a clear
    // message rather than scraping binary noise.
    if (fileName.endsWith('.xlsx') || fileName.endsWith('.xls')) {
      alert(enqueueSnackbar, "Excel files aren't supported yet — export to CSV and try again.", 'warning');
      return;
    }
    setMemberSaving(true);
    try {
      const text = await bulkImportFile.text();
      const emails = parseEmailsFromText(text);
      if (emails.length === 0) {
        alert(enqueueSnackbar, 'No emails found in the file.', 'warning');
        return;
      }
      // Resolve email → account in one batched call per 200 (the server's
      // per-request cap), rather than one request per email.
      const matchedByEmail = new Map<string, Login>();
      for (let i = 0; i < emails.length; i += 200) {
        const batch = emails.slice(i, i + 200);
        try {
          const result = await usersClient.searchUsersPaged(
            undefined, undefined, undefined, undefined, 1, batch.length, undefined, undefined, batch,
          );
          for (const u of result.items ?? []) {
            const key = u.email?.toLowerCase();
            if (key) matchedByEmail.set(key, u);
          }
        } catch {
          // Best-effort — an unresolved batch just counts as missing below.
        }
      }
      const candidateIds = [...matchedByEmail.values()]
        .map((u) => u.id)
        .filter((id): id is number => !!id && !members.some((m) => m.userId === id));
      const missing = emails.filter((e) => !matchedByEmail.has(e)).length;

      // Apply seat-limit cap if set.
      let toAdd = candidateIds;
      const capped = seatLimit != null && candidateIds.length > seatsRemaining(0);
      if (capped) {
        toAdd = candidateIds.slice(0, seatsRemaining(0));
      }

      if (toAdd.length === 0) {
        const matchedAll = missing === 0;
        alert(
          enqueueSnackbar,
          capped
            ? `Seat limit reached — couldn't add any new learners.`
            : matchedAll
              ? 'Everyone in the file is already a learner here'
              : 'No matching learners found',
          'warning',
        );
        return;
      }
      await groupsClient.addUsersToGroup(groupId, { userIds: toAdd });
      const refreshed = await groupsClient.getGroupMembers(groupId);
      setMembers(refreshed ?? []);
      setBulkImportFile(null);
      setBulkImportOpen(false);
      const skipped = missing + (capped ? candidateIds.length - toAdd.length : 0);
      alert(
        enqueueSnackbar,
        `Added ${toAdd.length}${skipped ? ` · ${skipped} skipped` : ''}`,
        skipped ? 'warning' : 'success',
      );
    } catch (err: any) {
      alert(enqueueSnackbar, err?.response?.data || 'Bulk import failed', 'error');
    } finally {
      setMemberSaving(false);
    }
  };

  const handleRemoveMember = async (userId: number) => {
    try {
      await groupsClient.removeUsersFromGroup(groupId, { userIds: [userId] });
      setMembers((cur) => cur.filter((m) => m.userId !== userId));
    } catch (err: any) {
      alert(enqueueSnackbar, err?.response?.data || 'Failed to remove learner', 'error');
    }
  };

  return (
    <Box sx={{ maxWidth: 960, display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Top action strip — single-add CTA + bulk import, with the
          seat-limit indicator on the right when set. */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setPickedLearner(null);
            setAddLearnerOpen(true);
          }}
          sx={{
            color: '#fff',
            '&.Mui-disabled': { color: '#fff', opacity: 0.65 },
            px: 2.5,
          }}
        >
          Add Learner
        </Button>
        <Button
          variant="outlined"
          startIcon={<UploadIcon />}
          onClick={() => {
            setBulkImportFile(null);
            setBulkImportOpen(true);
          }}
        >
          Bulk Import
        </Button>
        <Box sx={{ flex: 1 }} />
        {seatLimit != null && (
          <Chip
            size="small"
            label={`${members.length} / ${seatLimit} seats`}
            color={members.length >= seatLimit ? 'error' : 'default'}
            sx={{
              height: 24,
              fontWeight: 600,
              bgcolor: members.length >= seatLimit ? undefined : 'grey.100',
            }}
          />
        )}
      </Box>

      {/* Member list */}
      <Box>
        {/* Search field above the list — filters the rendered rows
            client-side. */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Search learners…"
            value={memberFilter}
            onChange={(e) => setMemberFilter(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                </InputAdornment>
              ),
            }}
            sx={{ width: 280 }}
          />
          <Box sx={{ flex: 1 }} />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle2" fontWeight={700} color="text.secondary">
            CURRENT LEARNERS
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {members.length > 0 && (
              <Typography variant="caption" color="text.secondary">
                {members.length} total
              </Typography>
            )}
            {members.length > 1 && (
              // SelectField wraps MUI Autocomplete and takes an `options`
              // prop — NOT MenuItem children. Passing children left it with
              // an empty option list, so it showed the auto "All" empty
              // entry and rendered blank for the real sort value.
              <SelectField
                size="small"
                value={memberSort}
                onChange={(e) => setMemberSort((e.target.value || 'name-asc') as MemberSort)}
                options={MEMBER_SORT_OPTIONS}
                allowEmpty={false}
                sx={{ minWidth: 200 }}
              />
            )}
          </Box>
        </Box>
        {membersLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : members.length === 0 ? (
          <Box
            sx={{
              p: 3,
              borderRadius: 2,
              border: '1px dashed',
              borderColor: 'divider',
              bgcolor: 'grey.50',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              color: 'text.secondary',
            }}
          >
            <PersonIcon fontSize="small" />
            <Typography variant="body2">No learners yet.</Typography>
          </Box>
        ) : (
          // Columnar member table — CSS grid keeps Name / Email / Last
          // sign on / Remove aligned. Horizontal scroll on narrow viewports.
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              overflow: 'auto',
            }}
          >
            {/* Sticky header row */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '2fr 2fr 1.5fr 48px',
                alignItems: 'center',
                gap: 1.5,
                px: 2,
                py: 1.25,
                bgcolor: 'grey.50',
                borderBottom: '1px solid',
                borderColor: 'divider',
                minWidth: 600,
              }}
            >
              <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Name
              </Typography>
              <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Email
              </Typography>
              <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Last sign-on
              </Typography>
              <Box />
            </Box>

            {sortedMembers.map((m, idx) => {
              const displayName = [m.userFirstName, m.userLastName].filter(Boolean).join(' ');
              const initial = (displayName || m.userEmail || '?').slice(0, 1).toUpperCase();
              const lastLogin = m.userId ? memberDetails[m.userId]?.lastLoginAt : null;
              return (
                <Box
                  key={m.userId}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '2fr 2fr 1.5fr 48px',
                    alignItems: 'center',
                    gap: 1.5,
                    px: 2,
                    py: 1.25,
                    borderBottom: idx === sortedMembers.length - 1 ? 0 : '1px solid',
                    borderColor: 'divider',
                    minWidth: 600,
                    '&:hover': { bgcolor: 'action.hover' },
                    transition: 'background-color 120ms ease',
                  }}
                >
                  {/* Name column — avatar + display name. */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                    <Avatar
                      sx={{
                        width: 32,
                        height: 32,
                        bgcolor: 'rgba(var(--brand-rgb), 0.18)',
                        color: 'primary.main',
                        flexShrink: 0,
                        fontSize: '0.85rem',
                        fontWeight: 700,
                      }}
                    >
                      {initial}
                    </Avatar>
                    <Typography variant="body2" fontWeight={600} noWrap>
                      {displayName || `User #${m.userId}`}
                    </Typography>
                    {/* Owner chip — surfaces the group's owner at a glance. */}
                    {ownerUserId === m.userId && (
                      <Chip
                        label="Owner"
                        size="small"
                        sx={{
                          ml: 0.5,
                          height: 20,
                          fontSize: '0.65rem',
                          fontWeight: 700,
                          bgcolor: 'rgba(var(--brand-rgb), 0.18)',
                          color: 'primary.main',
                          '& .MuiChip-label': { px: 0.75 },
                        }}
                      />
                    )}
                  </Box>

                  {/* Email */}
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {m.userEmail || '—'}
                  </Typography>

                  {/* Last sign-on */}
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {lastLogin ? formatDateTime(lastLogin as any) : '—'}
                  </Typography>

                  {/* Actions kebab */}
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Tooltip title="Actions">
                      <IconButton
                        size="small"
                        onClick={(e) => setRowMenu({ anchorEl: e.currentTarget, row: m })}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>

      {/* ── Row-action menu — single anchor reused across all member rows.
          Mirrors the Users page plus the group-specific "Remove from
          group" option. ──────────────────────────────────────────── */}
      <Menu
        anchorEl={rowMenu?.anchorEl ?? null}
        open={!!rowMenu}
        onClose={closeRowMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { minWidth: 220, borderRadius: 1.5 } }}
      >
        <MenuItem
          onClick={() => {
            if (rowMenu) setAssignTarget(loginFromRow(rowMenu.row));
            closeRowMenu();
          }}
        >
          <ListItemIcon>
            <BookmarkIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Assign content</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (rowMenu) memberHandleEdit(rowMenu.row);
            closeRowMenu();
          }}
        >
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (rowMenu) memberHandleResetPassword(rowMenu.row);
            closeRowMenu();
          }}
        >
          <ListItemIcon>
            <LockResetIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Reset Password</ListItemText>
        </MenuItem>
        {/* Resend Invitation — only when the user hasn't signed in yet. */}
        {(() => {
          const row = rowMenu?.row;
          if (!row) return null;
          const detail = row.userId ? memberDetails[row.userId] : undefined;
          const lastLogin = detail?.lastLoginAt;
          if (lastLogin) return null;
          return (
            <MenuItem
              onClick={() => {
                memberHandleResendInvitation(row);
                closeRowMenu();
              }}
            >
              <ListItemIcon>
                <EmailIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Resend Invitation</ListItemText>
            </MenuItem>
          );
        })()}
        <MenuItem
          onClick={() => {
            if (rowMenu) memberHandleToggleActive(rowMenu.row);
            closeRowMenu();
          }}
        >
          <ListItemIcon>
            {rowMenu?.row?.userId && memberDetails[rowMenu.row.userId]?.isActive === false ? (
              <PersonAddIcon fontSize="small" />
            ) : (
              <PersonOffIcon fontSize="small" />
            )}
          </ListItemIcon>
          <ListItemText>
            {rowMenu?.row?.userId && memberDetails[rowMenu.row.userId]?.isActive === false
              ? 'Enable Access'
              : 'Disable Access'}
          </ListItemText>
        </MenuItem>
        <Divider />
        {/* Group-specific destructive action — visually separated. */}
        <MenuItem
          onClick={() => {
            if (rowMenu?.row?.userId) handleRemoveMember(rowMenu.row.userId);
            closeRowMenu();
          }}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon sx={{ color: 'error.main' }}>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Remove learner from group</ListItemText>
        </MenuItem>
      </Menu>

      <ResetPasswordDialog
        open={resetPasswordOpen}
        user={resetPasswordTarget}
        onClose={() => {
          setResetPasswordOpen(false);
          setResetPasswordTarget(null);
        }}
        onSuccess={() => {
          setResetPasswordOpen(false);
          setResetPasswordTarget(null);
        }}
      />

      <AssignContentDialog
        open={!!assignTarget}
        targetUsers={assignTarget ? [assignTarget] : []}
        onClose={() => setAssignTarget(null)}
      />

      {/* ── Add learner dialog — autocomplete picker. */}
      <Dialog
        open={addLearnerOpen}
        onClose={() => !memberSaving && setAddLearnerOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" fontWeight={700}>
            Add a learner
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Search by name or email and add them to {groupName || 'this group'}.
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ pt: 1 }}>
            <UserAutocomplete
              value={pickedLearner}
              onChange={(v) => setPickedLearner(Array.isArray(v) ? v[0] ?? null : v)}
              placeholder="Find a learner…"
            />
            {seatLimit != null && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {seatsRemaining(0)} of {seatLimit} seats remaining.
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setAddLearnerOpen(false)} color="inherit" disabled={memberSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddPickedLearner}
            disabled={!pickedLearner || memberSaving}
            startIcon={memberSaving ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
            sx={{
              color: '#fff',
              '&.Mui-disabled': { color: '#fff', opacity: 0.65 },
              px: 3,
            }}
          >
            Add to Group
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Bulk import dialog — spreadsheet upload. */}
      <Dialog
        open={bulkImportOpen}
        onClose={() => !memberSaving && setBulkImportOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" fontWeight={700}>
            Bulk import learners
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Upload a CSV or text file. Every email-looking value is read and
            matched against existing user accounts.
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Button
              variant="outlined"
              component="label"
              startIcon={<UploadIcon />}
              sx={{ alignSelf: 'flex-start' }}
            >
              {bulkImportFile ? 'Replace file' : 'Choose file'}
              <input
                hidden
                type="file"
                accept=".csv,.tsv,.txt,text/csv,text/plain"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) setBulkImportFile(file);
                }}
              />
            </Button>
            {bulkImportFile && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  p: 1.5,
                  borderRadius: 1.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'grey.50',
                }}
              >
                <FileTextIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={600} noWrap>
                    {bulkImportFile.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {(bulkImportFile.size / 1024).toFixed(1)} KB
                  </Typography>
                </Box>
                <IconButton size="small" onClick={() => setBulkImportFile(null)}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
            <Box
              sx={{
                p: 1.5,
                borderRadius: 1.5,
                bgcolor: 'warning.softBg',
                border: '1px solid #ffe082',
                color: 'warning.softText',
              }}
            >
              <Typography variant="caption" sx={{ color: 'inherit' }}>
                <strong>Format:</strong> CSV, TSV, or plain text — any cell that
                looks like an email is picked up. For Excel files, export to CSV
                first. Unknown emails are skipped with a notice.
                {seatLimit != null && (
                  <>
                    {' '}Adds are capped at the seat limit ({seatsRemaining(0)} seats
                    remaining).
                  </>
                )}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setBulkImportOpen(false)} color="inherit" disabled={memberSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleBulkImport}
            disabled={!bulkImportFile || memberSaving}
            startIcon={memberSaving ? <CircularProgress size={16} color="inherit" /> : <UploadIcon />}
            sx={{
              color: '#fff',
              '&.Mui-disabled': { color: '#fff', opacity: 0.65 },
              px: 3,
            }}
          >
            Import
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
